import { Box, Button, Chip, Stack } from "@mui/material";
import {
  TripSortCriteria,
  GetBusinessEntityListQuery,
  BusinessEntityType,
  NewBusinessEntityInputWithType,
  CustomFieldContext,
  AssetStatus,
} from "../../../graphql/generated";
import formatPhoneNumber from "../../../utils/labels/formatPhoneNumber";
import { EnumTableField, TableField } from "../../common/LynksTable/LynksTable";
import SmartLynksTable, {
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import ExcelImporter from "../../common/ExcelImporter";
import suppliersMapping from "../../../utils/mapping/suppliers";
import businessEntitySchema from "../BusinessEntityForm/businessEntitySchema";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { testIds } from "./SuppliersList.testIds";

type SupplierListItem =
  GetBusinessEntityListQuery["businessEntities"]["data"][0];

export default function SuppliersList({
  query,
  onBulkCreate,
}: {
  query: QueryFn<SupplierListItem, "businessEntities", TripSortCriteria>;
  onBulkCreate: (data: NewBusinessEntityInputWithType[]) => Promise<any>;
}) {
  const { t } = useTranslation(["business", "common"]);

  const statusField: EnumTableField<SupplierListItem, AssetStatus> = useMemo(
    () => ({
      value: (supplier) => supplier.status,
      label: t("assets:status", "Status"),
      valueLabel: (status) => t(`common:statusTypes.${status}`),
      type: "enum",
      colors: {
        ACTIVE: "primary",
        INACTIVE: "default",
      },
      values: Object.values(AssetStatus),
      sortBy: "status",
    }),
    [t]
  );

  const fields: TableField<SupplierListItem, AssetStatus>[] = useMemo(
    () => [
      {
        value: (businessEntity) => {
          const allTypes = [
            businessEntity.type,
            ...(businessEntity.additionalTypes || []),
          ];
          if (allTypes.includes(BusinessEntityType.Broker)) {
            return `${businessEntity.code} ${
              businessEntity.mcNumber ? `(${businessEntity.mcNumber})` : ""
            }`;
          }
          return businessEntity.code;
        },
        label: t("business:code", "Code"),
        type: "string",
        sortBy: "code",
      },
      {
        value: "name",
        label: t("business:name", "Name"),
        type: "string",
      },
      statusField,
      {
        value: (supplier) => supplier.address?.label,
        label: t("business:address", "Address"),
        type: "string",
        sortBy: "address.label",
      },
      {
        value: (supplier) => formatPhoneNumber(supplier.contact.phoneNumber),
        subtitle: (supplier) => supplier.contact.email,
        label: t("business:contact", "Contact"),
        type: "string",
        sortBy: "contact.phoneNumber",
      },
      {
        value: (supplier) => (
          <Stack direction="row" spacing={1}>
            {supplier.tags?.map((tag) => (
              <Chip label={tag} />
            ))}
          </Stack>
        ),
        sortBy: "tags",
        label: t("common:tags"),
        type: "string",
      },
    ],
    [t, statusField]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          height: "calc(100% - 74px)",
        }}
      >
        <SmartLynksTable
          query={query}
          dataKey="businessEntities"
          additionalQueryVariables={{
            businessEntityTypes: [BusinessEntityType.Supplier],
            includeInactive: true,
          }}
          fields={fields}
          detailsUrlPrefix="/suppliers/details"
          customFieldContext={[CustomFieldContext.Supplier]}
          id="suppliers-list"
          selectable
          showGroups
          renderHeaderActions={() => (
            <Stack direction="row" spacing={2}>
              <ExcelImporter
                mapping={suppliersMapping}
                schema={businessEntitySchema}
                onBulkCreate={onBulkCreate}
              />
              <Button data-testid={testIds.suppliersListNewSupplier}
                href="/suppliers/new"
                variant="contained"
                color="secondary"
                size="large"
                id="new-supplier-button"
                aria-label="New Supplier"
              >
                {t("business:supplier.new", "New Supplier")}
              </Button>
            </Stack>
          )}
        />
      </Box>
    </div>
  );
}
